// Value coercion + validation per property kind.
// Used by the materialization edge function and by the client-side preview.
// Each coerce function returns the typed value or throws a descriptive error.

import type { PropertyKind, PropertyType } from "@/types/ontology";

/** Result of a coercion attempt for a single value. */
export type CoercionResult = { ok: true; value: unknown } | { ok: false; error: string };

/**
 * Coerce a raw string value from a CSV cell into the typed value
 * expected by the given property kind.
 */
export function coerce(
  raw: string,
  property: Pick<PropertyType, "kind" | "required" | "enum_values" | "ref_object_type_id">
): CoercionResult {
  const trimmed = raw.trim();

  // Handle empty / null values.
  if (trimmed === "" || trimmed === null) {
    if (property.required) {
      return { ok: false, error: `required value is missing` };
    }
    return { ok: true, value: null };
  }

  switch (property.kind as PropertyKind) {
    case "string":
      return { ok: true, value: trimmed };

    case "number":
      return coerceNumber(trimmed);

    case "date":
      return coerceDate(trimmed);

    case "bool":
      return coerceBool(trimmed);

    case "currency":
      // Currency stores as a number (the monetary value); currency code is metadata on the property.
      return coerceNumber(trimmed.replace(/[$£€¥,\s]/g, ""));

    case "enum":
      return coerceEnum(trimmed, property.enum_values ?? []);

    case "ref":
      // ref is a pk_value of another object type — store as a string.
      // Validation against actual objects happens during materialization.
      return { ok: true, value: trimmed };

    default:
      return { ok: false, error: `unknown property kind: ${String(property.kind)}` };
  }
}

// ── Internal helpers ──────────────────────────────────────────────────────────

function coerceNumber(raw: string): CoercionResult {
  const n = Number(raw);
  if (isNaN(n)) {
    return { ok: false, error: `"${raw}" is not a valid number` };
  }
  return { ok: true, value: n };
}

// ISO 8601 date test — accepts YYYY-MM-DD and also common variants like MM/DD/YYYY.
const ISO_DATE = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})?)?$/;
const US_DATE = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/;

function coerceDate(raw: string): CoercionResult {
  if (ISO_DATE.test(raw)) {
    const d = new Date(raw);
    if (!isNaN(d.getTime())) {
      return { ok: true, value: raw };
    }
  }

  const usMatch = US_DATE.exec(raw);
  if (usMatch) {
    const [, month, day, year] = usMatch;
    const fullYear = year.length === 2 ? `20${year}` : year;
    const isoStr = `${fullYear}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    const d = new Date(isoStr);
    if (!isNaN(d.getTime())) {
      return { ok: true, value: isoStr };
    }
  }

  return {
    ok: false,
    error: `"${raw}" is not a recognised date (expected YYYY-MM-DD or MM/DD/YYYY)`,
  };
}

function coerceBool(raw: string): CoercionResult {
  const lower = raw.toLowerCase();
  if (["true", "yes", "1", "on"].includes(lower)) return { ok: true, value: true };
  if (["false", "no", "0", "off"].includes(lower)) return { ok: true, value: false };
  return { ok: false, error: `"${raw}" is not a valid boolean (expected true/false/yes/no/1/0)` };
}

function coerceEnum(raw: string, allowed: string[]): CoercionResult {
  if (allowed.includes(raw)) {
    return { ok: true, value: raw };
  }
  return {
    ok: false,
    error: `"${raw}" is not in the allowed values: [${allowed.join(", ")}]`,
  };
}
